import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '@/hooks/useApiMutation';
import { useApiQuery } from '@/hooks/useApiQuery';
import { apiDelete, apiGet, apiPatch, apiPost, operationsClient } from '@/lib/api';
import { noteEndpoints } from '@/lib/api/endpoints';
import { noteKeys } from '@/lib/queryKeys';
import type {
  CreateEntityNoteDto,
  EntityNote,
  NoteEntityType,
  NotesListResponse,
  UpdateEntityNoteDto,
} from '@/types/note';

function normalizeNotes(raw: NotesListResponse | EntityNote[] | unknown): EntityNote[] {
  if (Array.isArray(raw)) return raw;
  if (!raw || typeof raw !== 'object') return [];

  const response = raw as NotesListResponse;
  if (Array.isArray(response.data)) return response.data;
  if (Array.isArray(response.items)) return response.items;
  return [];
}

export function useEntityNotes(entityType: NoteEntityType, entityId: string) {
  return useApiQuery<EntityNote[]>({
    queryKey: noteKeys.entity(entityType, entityId),
    queryFn: async () => {
      const raw = await apiGet<NotesListResponse | EntityNote[]>(
        operationsClient,
        noteEndpoints.entity(entityType, entityId),
        { params: { limit: 50, sortBy: 'createdAt', sortDir: 'desc' } },
      );
      return normalizeNotes(raw);
    },
    enabled: Boolean(entityType && entityId),
    showErrorToast: false,
  });
}

export function useCreateEntityNote(entityType: NoteEntityType, entityId: string) {
  const queryClient = useQueryClient();

  return useApiMutation<EntityNote, CreateEntityNoteDto>({
    mutationFn: (dto) =>
      apiPost<EntityNote, CreateEntityNoteDto>(
        operationsClient,
        noteEndpoints.entity(entityType, entityId),
        dto,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: noteKeys.entity(entityType, entityId) });
      toast.success('Note added');
    },
  });
}

export function useUpdateEntityNote(entityType: NoteEntityType, entityId: string) {
  const queryClient = useQueryClient();

  return useApiMutation<EntityNote, { noteId: string; dto: UpdateEntityNoteDto }>({
    mutationFn: ({ noteId, dto }) =>
      apiPatch<EntityNote, UpdateEntityNoteDto>(
        operationsClient,
        noteEndpoints.detail(noteId),
        dto,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: noteKeys.entity(entityType, entityId) });
      toast.success('Note updated');
    },
  });
}

export function useDeleteEntityNote(entityType: NoteEntityType, entityId: string) {
  const queryClient = useQueryClient();

  return useApiMutation<{ deleted: boolean }, string>({
    mutationFn: (noteId) =>
      apiDelete<{ deleted: boolean }>(operationsClient, noteEndpoints.detail(noteId)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: noteKeys.entity(entityType, entityId) });
      toast.success('Note deleted');
    },
  });
}
